import {
  PaginatedQueryResult,
  useTableState,
  useFilter,
  useMap,
  useGetResource,
  useCurrentUserResource,
  useOptimize,
  useCompany,
  useExportReport,
  useExportNotification,
} from '@dispatch/shared/hooks';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  MultiSelectOption,
  TaskListFilterGroup,
  TasksListFilter,
  TaskListParams,
  MapContext,
  GeoJSONObject,
  Resource,
  TaskInfo,
  AttributeOptions,
  ResourceLocation,
  ResultStatus,
  Actions,
  Subjects,
  TaskType,
  ReportType,
  ReportFormat,
  TaskFilterGroup,
  TaskPeril,
} from '@dispatch/shared/types';
import {
  boldLinkColumn,
  dateColumn,
  DynamicFilterModal,
  PageActionGroup,
  PageBody,
  PageHeader,
  PageHeaderRow,
  PageLayout,
  PageTitle,
  QuickFilters,
  ResolutionChip,
  TableComponent,
  TableProps,
  textColumn,
  Filter,
  Loading,
  ConfirmModal,
  Form,
  RenderFuncProps,
  TextField,
  useNotification,
  SelectField,
} from '@dispatch/web/ui';
import {
  Button,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Slide,
  Stack,
  Switch,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import {
  GridColDef,
  GridSortModel,
  GridSelectionModel,
} from '@mui/x-data-grid-pro';
import { addDays, differenceInDays, format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AssignedResourceChip } from './components/AssignedResourceChip';
import {
  groupTaskReducer,
  itemToGeoJSONPoint,
  getTimeWithOffset,
  ResourceLocationToGeoJSONPoint,
  padClaimNumber,
} from '@dispatch/shared';
import { DispatchMap } from '../mapView';
import { useIsFetching } from 'react-query';
import { GeoStatusChip } from '../shared/GeoStatusChip';
import { MethodOfInspectionChip } from './components/MethodOfInspectionChip';
import { TaskInfoWithAddress } from '@dispatch/shared/services';
import { Link, useNavigate } from 'react-router-dom';
import { Ability } from '@casl/ability';
import { isNil, isNull } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import { useDebounce } from 'react-use';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCurrentUser } from '@dispatch/web/auth';
import { FileDownload } from '@mui/icons-material';
export interface TasksListProps {
  data: PaginatedQueryResult<TaskInfoWithAddress[], unknown>;
  onFilterChange: (filters: TasksListFilter) => void;
  filters: TasksListFilter;
  onRefresh: () => void;
  filterOptions: Record<string, MultiSelectOption[]>;
  storedFilters: any;
  handleSaveFilters: (filters: unknown) => void;
  handleOrderIndexBulkUpdate: (
    values: (Omit<TasksListFilter, 'pageSize'> & { isNew?: boolean })[]
  ) => void;
  handleGroupsOrderIndexBulkUpdate: (values: TaskFilterGroup[]) => void;
  handleSaveFilterGroup: (values: TaskFilterGroup) => void;
  handleDeleteFilter: (filterId: string) => void;
  handleDeleteFilterGroup: (groupId: string) => void;
  handleSort?: (value: GridSortModel) => void;
  viewState: TaskListParams;
  setViewState: React.Dispatch<React.SetStateAction<TaskListParams>>;
  setResourceFilterInput: React.Dispatch<React.SetStateAction<string>>;
  setLossCountyFilterInput: React.Dispatch<React.SetStateAction<string>>;
  setLossStateFilterInput: React.Dispatch<React.SetStateAction<string[]>>;
  setRsrcCountyFilterInput: React.Dispatch<React.SetStateAction<string>>;
  setRsrcStateFilterInput: React.Dispatch<React.SetStateAction<string[]>>;
  filteredResources: PaginatedQueryResult<Resource[], unknown>;
  mappedFilteredResources: MultiSelectOption[];
  filteredLossCounties: MultiSelectOption[];
  filteredLossCountiesStatus: ResultStatus;
  filteredRsrcCounties: MultiSelectOption[];
  filteredRsrcCountiesStatus: ResultStatus;
  companyAttributes?: AttributeOptions[];
  multiSelected?: Resource[];
  selectedResourcesLoading?: boolean;
  permissions: Ability<[Actions, Subjects]> | undefined;
  unassignedCount: number;
  attributeOptions?: {
    title: string;
    value: string;
  }[];
  isWorkingTimeFilteredState: {
    isNonWorkingTimeFiltered: boolean;
    setIsNonWorkingTimeFiltered: React.Dispatch<React.SetStateAction<boolean>>;
  };
  showFilterState: {
    showFilterState: Record<string, boolean>;
    setShowFilterState: React.Dispatch<
      React.SetStateAction<Record<string, boolean>>
    >;
  };
  perils: TaskPeril[] | undefined;
}

export function TasksList({
  data,
  filters: listFilters,
  onFilterChange,
  onRefresh,
  filterOptions,
  storedFilters,
  handleSaveFilters,
  handleOrderIndexBulkUpdate,
  handleGroupsOrderIndexBulkUpdate,
  handleSaveFilterGroup,
  handleDeleteFilter,
  handleDeleteFilterGroup,
  handleSort,
  viewState,
  setViewState,
  setResourceFilterInput,
  setLossCountyFilterInput,
  setLossStateFilterInput,
  setRsrcCountyFilterInput,
  setRsrcStateFilterInput,
  filteredResources,
  filteredLossCounties,
  filteredLossCountiesStatus,
  filteredRsrcCounties,
  filteredRsrcCountiesStatus,
  mappedFilteredResources,
  companyAttributes,
  multiSelected,
  selectedResourcesLoading,
  permissions,
  unassignedCount,
  attributeOptions,
  isWorkingTimeFilteredState,
  showFilterState,
  perils,
}: TasksListProps) {
  const { company, featureFlags } = useCompany();
  const user = useCurrentUser();
  const userId = String(user?.userId ?? '');
  const navigate = useNavigate();
  const fetching = useIsFetching();
  const filterContext = useFilter();
  const mapContext: MapContext = useMap();
  const { tableState, setTableState } = useTableState();
  const { quickFilters, filters } = useFilter();
  const { optimize, status: optimizeStatus } = useOptimize();
  const [debouncedSelectedResource, setDebouncedSelectedResource] =
    React.useState<{
      id?: string;
    }>({ id: '' });

  useDebounce(
    () => setDebouncedSelectedResource({ id: mapContext.selectedResourceId }),
    500,
    [mapContext.selectedResourceId]
  );

  const { data: resource, status: resourceStatus } = useGetResource({
    id: debouncedSelectedResource.id || '',
    company,
  });

  const getPaginationConfig = (pageSize: number) => ({
    pageSize,
    pagingOptions: [5, 10, 15, 20],
  });

  const handleDateRangeValues = (field: string) => {
    return field ? format(new Date(field), 'yyyy-MM-dd') : undefined;
  };

  const finalFilters = useMemo(() => {
    // Flatten nested taskFilters from context into a flat object for API mapping.
    // Context stores as { taskFilters: { lossStates: [...], ... } } due to contextKey.
    const taskFilters = filters?.taskFilters || {};
    const flatFilters = { ...filters, ...taskFilters } as Record<string, any>;

    let lossDateFrom;
    let lossDateTo;
    let appointmentDateFrom;
    let appointmentDateTo;
    let noticeDateFrom;
    let noticeDateTo;
    let receivedDateFrom;
    let receivedDateTo;

    if (flatFilters?.lossDate?.[0]) {
      lossDateFrom = handleDateRangeValues(flatFilters?.lossDate?.[0]);
    }
    if (flatFilters?.lossDate?.[1]) {
      lossDateTo = handleDateRangeValues(flatFilters?.lossDate?.[1]);
    }

    if (flatFilters?.appointmentDate?.[0]) {
      appointmentDateFrom = handleDateRangeValues(
        flatFilters?.appointmentDate?.[0]
      );
    }
    if (flatFilters?.appointmentDate?.[1]) {
      appointmentDateTo = handleDateRangeValues(
        flatFilters?.appointmentDate?.[1]
      );
    }

    if (flatFilters?.noticeDate?.[0]) {
      noticeDateFrom = handleDateRangeValues(flatFilters?.noticeDate?.[0]);
    }
    if (flatFilters?.noticeDate?.[1]) {
      noticeDateTo = handleDateRangeValues(flatFilters?.noticeDate?.[1]);
    }

    if (flatFilters?.receivedDate?.[0]) {
      receivedDateFrom = handleDateRangeValues(flatFilters?.receivedDate?.[0]);
    }
    if (flatFilters?.receivedDate?.[1]) {
      receivedDateTo = handleDateRangeValues(flatFilters?.receivedDate?.[1]);
    }

    if (flatFilters) {
      flatFilters.qualifiedResourceIds = [];
    }

    const hideCancelledTasks = () => {
      if (
        quickFilters?.assignedStatus === 'unassigned' &&
        (!flatFilters?.queueName || flatFilters?.queueName?.length === 0)
      ) {
        return true;
      }

      return false;
    };

    // Strip `teams` from the listFilters spread — saved filters coming from
    // Scheduler View carry `teams` at the root, but the API key is
    // `assignedResourceTeamIds` (assigned below). Without this, both would
    // end up on the payload.
    const { teams: _teams, ...restListFilters } = listFilters || {};

    const mappedFilters = {
      ...restListFilters,
      assignedResourceIds: flatFilters?.assignedResourceIds?.map?.(
        (r: MultiSelectOption | string) =>
          typeof r === 'string' ? r : r?.value
      ),
      qualifiedResourceIds: flatFilters?.qualifiedResourceIds?.map?.(
        (r: MultiSelectOption | string) =>
          typeof r === 'string' ? r : r?.value
      ),
      lossDateFrom: lossDateFrom,
      lossDateTo: lossDateTo,
      lossCounties: flatFilters?.lossCounties,
      appointmentDateFrom:
        flatFilters?.appointmentDate?.[0] && appointmentDateFrom,
      appointmentDateTo: flatFilters?.appointmentDate?.[1] && appointmentDateTo,
      noticeDateFrom: flatFilters?.noticeDate?.[0] && noticeDateFrom,
      noticeDateTo: flatFilters?.noticeDate?.[1] && noticeDateTo,
      receivedDateFrom: flatFilters?.receivedDate?.[0] && receivedDateFrom,
      receivedDateTo: flatFilters?.receivedDate?.[1] && receivedDateTo,
      lossStates: flatFilters?.lossStates,
      lossZipCodes: flatFilters?.lossZipCodes,
      methodsOfInspection: flatFilters?.methodsOfInspection,
      perils: flatFilters?.perils,
      claimNumber: flatFilters?.claimNumber || listFilters.claimNumber,
      status: quickFilters?.assignedStatus,
      lossRegions: flatFilters?.lossRegions,
      assignedResourceTeamIds: flatFilters?.teams,
      assignedResourceOfficeIds: flatFilters?.offices,
      assignedResourceGroupIds: flatFilters?.groups,
      assignedResourceRoleIds: flatFilters?.roles,
      catCode: flatFilters?.catCode,
      initialOrSupplement: flatFilters?.initialOrSupplement || null,
      queueName: flatFilters?.queueName,
      taskTypes: flatFilters?.taskTypes,
      hasCatCode: flatFilters?.hasCatCode,
      hideCancelledTasks: hideCancelledTasks(),
      claimAttributes: flatFilters?.claimAttributes,
    };

    const nonNullFilters = Object.entries(mappedFilters)?.filter(
      (v) =>
        v[0].toLowerCase().includes('date') &&
        !v[0].toLowerCase().includes('schedule') &&
        !isNil(v[1])
    );

    if (nonNullFilters?.length > 0) {
      const start = nonNullFilters?.filter((v) =>
        v[0].toLowerCase().includes('from')
      );
      const end = nonNullFilters?.filter((v) =>
        v[0].toLowerCase().includes('to')
      );

      if (start?.length > 0) mappedFilters.scheduleStartDate = start[0][1];
      if (end?.length > 0) mappedFilters.scheduleEndDate = end[0][1];
    }

    if (nonNullFilters?.length <= 0) {
      if (
        mappedFilters.scheduleStartDate &&
        mappedFilters.scheduleEndDate &&
        differenceInDays(
          new Date(mappedFilters.scheduleEndDate),
          new Date(mappedFilters.scheduleStartDate)
        ) < 6
      ) {
        if (mappedFilters?.scheduleStartDate) {
          mappedFilters.scheduleEndDate = new Date(
            addDays(new Date(mappedFilters.scheduleStartDate), 6)
          ).toISOString();
        }
      }
    }

    return mappedFilters;
  }, [filters, quickFilters?.assignedStatus]);

  useEffect(() => {
    onFilterChange(finalFilters);
    data?.goToFirstPage();
  }, [finalFilters]);

  const EMPTY_DATETIMEOFFSET = '0001-01-01T00:00:00+00:00';

  const renderAction = (row: { taskType: string; claimNumber: string }) => {
    switch (row?.taskType) {
      case 'COMPLEX':
      case 'COMPLEX_CHILD':
        if (
          isNull(row?.taskType) ||
          !['COMPLEX_CHILD', 'COMPLEX'].includes(
            row?.taskType.toUpperCase() || ''
          ) ||
          !permissions?.can?.('access', 'complex-loss')
        )
          return;

        return (
          // on click will take you to the large loss tab, and add filter for claimNumber to see the task in the large loss view
          <Link to={`/complex-loss?claimNumber=${row.claimNumber}`}>
            <Button variant="text" size="small">
              View Complex Loss
            </Button>
          </Link>
        );
      case 'CLAIM_OWNER':
        return (
          // on click will take you to the large loss tab, and add filter for claimNumber to see the task in the large loss view
          <Button variant="text" size="small" disabled={true}>
            Claim Owner
          </Button>
        );
      default:
        return;
    }
  };

  const columns: GridColDef[] = [
    boldLinkColumn<TaskInfo, string>(
      {
        field: 'claimNumber',
        headerName: 'Claim Number',
        valueGetter: (params) => params.row?.claimNumber,
        flex: 1,
        sortable: false,
      },
      (task, _rowValue) => {
        return `/tasks/${task?.id}?tab=0`;
      }
    ),
    textColumn({
      field: 'insuredName',
      headerName: 'Insured Name',
      flex: 1,
      sortable: false,
    }),
    textColumn({
      field: 'location',
      headerName: 'Location',
      flex: 0.1,
      sortable: false,
    }),
    textColumn({
      field: 'lossCounty',
      headerName: 'County',
      flex: 0.1,
      sortable: false,
    }),
    textColumn({
      field: 'peril',
      headerName: 'Peril',
      flex: 0.1,
      sortable: false,
      valueGetter: (params) =>
        perils?.find((p) => p.code === params.value)?.description,
    }),
    dateColumn({
      field: 'dateOfLoss',
      headerName: 'Date of Loss',
      valueGetter: (params) =>
        !!params?.row?.dateOfLoss &&
        params?.row?.dateOfLoss !== EMPTY_DATETIMEOFFSET
          ? parseISO(params?.row?.dateOfLoss)
          : '-',
      flex: 1,
    }),
    dateColumn({
      field: 'appointment',
      headerName: 'Appointment',
      flex: 1,
      valueGetter: (params) =>
        !!params?.row?.appointment &&
        params?.row?.appointment !== EMPTY_DATETIMEOFFSET &&
        params?.row?.taskType !== TaskType.CLAIM_OWNER
          ? getTimeWithOffset(params?.row?.timeZoneId, params?.row?.appointment)
          : '-',
    }),
    dateColumn({
      field: 'taskReceived',
      headerName: 'Date Received',
      valueGetter: (params) =>
        !!params?.row?.taskReceived &&
        params?.row?.taskReceived !== EMPTY_DATETIMEOFFSET
          ? parseISO(params?.row?.taskReceived)
          : '-',
      flex: 1,
    }),
    {
      field: 'assignedResourceId',
      headerName: 'Assigned Resource',
      renderCell: (params) => (
        <div>
          {!params.value && <p>-</p>}
          {params.value && (
            <AssignedResourceChip name={params.row.assignedResource} />
          )}
        </div>
      ),
      flex: 1,
      sortable: false,
    },
    {
      field: 'targetQueue',
      headerName: 'Status',
      renderCell: (params) => {
        const tooltipString =
          !params.row.lossLat || !params.row.lossLat
            ? 'Address is Incorrect'
            : '';
        return (
          <Stack direction="row" gap={1}>
            <ResolutionChip
              targetQueue={params.value}
              tooltipString={tooltipString}
            />
            <GeoStatusChip
              geoStatus={params.row.geoStatus}
              showIcon
              size="small"
            />
          </Stack>
        );
      },
      flex: 0.5,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'methodOfInspection',
      headerName: 'Method of Inspection',
      renderCell: (params) => (
        <div style={{ padding: '0, 5px' }}>
          <MethodOfInspectionChip methodOfInspection={params.value} />
        </div>
      ),
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: ({ row }) => renderAction(row),
      flex: 1,
      sortable: false,
    },
  ];

  const { uiValidationRules } = useCompany();

  const onSearch = (value: string) => {
    // Trim once up front so every downstream write (claimNumber, claimNumberRaw,
    // taskFilters.claimNumber) sees the same normalized input. Matches
    // SchedulerPage.handleSearch and padClaimNumber's own internal trim.
    const trimmed = value?.trim() || '';
    const newFilters = trimmed
      ? { ...listFilters, claimNumber: trimmed }
      : { ...listFilters, claimNumber: null };

    if (uiValidationRules?.padClaimNumber) {
      newFilters.claimNumber = padClaimNumber(trimmed);
    }

    filterContext.setFilters({
      ...filters,
      ...newFilters,
      // Keep the raw typed value at the filter root so the search field can
      // display it after nav between Task List and Scheduler. `claimNumber`
      // (padded) stays authoritative for API calls.
      claimNumberRaw: trimmed || null,
      taskFilters: {
        ...(filters?.taskFilters || {}),
        assignedResourceIds: filters?.taskFilters?.assignedResourceIds || [],
        claimNumber: newFilters.claimNumber,
      },
    });
    onFilterChange(newFilters);
    data?.goToFirstPage();
  };

  const tableArgs: TableProps<TaskInfo> = {
    // disable row selection when map view is not present
    dataGridProps: {
      disableSelectionOnClick: !viewState.showMap,
      onSelectionModelChange: (selection: GridSelectionModel) => {
        /**
         * When mapView is true this callback will fire when selecting a row.
         * The callback returns the current selected row id. This is used to find the current selected task, and
         * the task is then added to the map context as the popup info data.
         */
        const rowData = data?.data?.filter((row) => row.id === selection[0]);

        mapContext.setPopupInfo?.({
          ...rowData?.[0],
          lossLat: rowData?.[0]?.lossLat || 0,
          lossLong: rowData?.[0]?.lossLong || 0,
        });

        mapContext.setSelectedResourceId(rowData?.[0]?.assignedResourceId);
      },
      selectionModel: mapContext.popupInfo?.id && [mapContext.popupInfo.id],
    },
    tableState: {
      columnModel: tableState['tasks'].columnModel,
      pinnedColumns: {
        ...tableState['tasks'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        tasks: { ...tableState['tasks'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        tasks: { ...tableState['tasks'], pinnedColumns: newPinned },
      });
    },
    rows: data?.data || [],
    columns,
    height: 400,
    emptyEntityName: 'Task',
    status: data?.status,
    getRowId: (row) => row.id,
    serverPaging: {
      ...data,
      onPageSizeChange: (newPageSize) => {
        onFilterChange({ ...listFilters, pageSize: newPageSize });
      },
    },
    handleSort,
    pagination: getPaginationConfig(listFilters.pageSize || 25),
  };

  const { isAdjuster } = useCurrentUserResource();

  const dynamicFilterOptions: Filter[] = useMemo(() => {
    let filters: Filter[] = [
      {
        type: 'toggle',
        id: 'hasCatCode',
        name: 'hasCatCode',
        label: 'CAT Code Status',
        chipLabel: [
          {
            label: 'Has CAT Code',
            value: 'true',
          },
          {
            label: 'No CAT Code',
            value: 'false',
          },
        ],
        buttons: [
          {
            label: 'Has CAT Code',
            value: true,
          },
          {
            label: 'No CAT Code',
            value: false,
          },
        ],
      },
      {
        id: 'initialOrSupplement',
        name: 'initialOrSupplement',
        label: 'Assignment Category',
        type: 'toggle',
        chipLabel: [
          { label: 'Initial', value: 'initial' },
          { label: 'Supplement', value: 'supplement' },
        ],
        buttons: [
          {
            value: 'initial',
            label: 'Initial',
          },
          {
            value: 'supplement',
            label: 'Supplement',
          },
        ],
      },
      {
        id: 'lossDate',
        name: 'lossDate',
        label: 'Loss Date',
        start: 'lossDateFrom',
        end: 'lossDateTo',
        type: 'date-range',
      },
      {
        id: 'appointmentDate',
        name: 'appointmentDate',
        label: 'Appointment Date',
        type: 'date-range',
        start: 'appointmentDateFrom',
        end: 'appointmentDateTo',
      },
      {
        id: 'noticeDate',
        name: 'noticeDate',
        label: 'Notice Date',
        type: 'date-range',
        start: 'noticeDateFrom',
        end: 'noticeDateTo',
      },
      {
        id: 'receivedDate',
        name: 'receivedDate',
        label: 'Received Date',
        start: 'receivedDateFrom',
        end: 'receivedDateTo',
        type: 'date-range',
      },
      {
        id: 'catCode',
        name: 'catCode',
        label: 'CAT Code',
        type: 'text',
      },
      {
        id: 'queueName',
        name: 'queueName',
        label: 'Assignment Status',
        type: 'select',
        multi: true,
        options: [
          {
            value: 'auto_dispatch_q',
            title: 'Processing',
          },
          {
            value: 'manual_dispatch_q',
            title: 'Needs Dispatching',
          },
          {
            value: 'dispatched_q',
            title: 'Dispatched',
          },
          {
            value: 'needs_attention_q',
            title: 'Needs Attention',
          },
          {
            value: 'wip_q',
            title: 'Started',
          },
          {
            value: 'egress',
            title: 'Completed',
          },
          {
            value: 'cancelled_q',
            title: 'Cancelled',
          },
        ],
      },
      {
        type: 'async-select',
        id: 'assignedResourceIds',
        name: 'assignedResourceIds',
        label: 'Assigned Resource',
        multi: true,
        options: mappedFilteredResources,
        setInput: setResourceFilterInput,
        loading: filteredResources?.status === 'loading',
        tagLimit: 10,
      },
      {
        id: 'lossStates',
        name: 'lossStates',
        label: 'Loss States',
        type: 'select',
        multi: true,
        options: [
          {
            value: '',
            title: 'All',
          },
          ...filterOptions['statesOptions'],
        ],
      },
      {
        id: 'lossCounties',
        name: 'lossCounties',
        label: 'Loss County',
        type: 'async-select',
        multi: true,
        setInput: setLossCountyFilterInput,
        options: filteredLossCounties,
        loading: filteredLossCountiesStatus === 'loading',
      },
      {
        id: 'lossZipCodes',
        name: 'lossZipCodes',
        label: 'Loss Zip Code',
        type: 'text',
      },
      {
        id: 'teams',
        name: 'teams',
        label: 'Teams',
        type: 'select',
        multi: true,
        options: filterOptions['teamOptions'] || [],
      },
      {
        id: 'offices',
        name: 'offices',
        label: 'Offices',
        type: 'select',
        multi: true,
        options: filterOptions['officeOptions'] || [],
      },
      {
        id: 'groups',
        name: 'groups',
        label: 'Groups',
        type: 'select',
        multi: true,
        options: filterOptions['groupOptions'] || [],
      },
      {
        id: 'lossRegions',
        name: 'lossRegions',
        label: 'Regions',
        type: 'select',
        multi: true,
        options: filterOptions['regionOptions'] || [],
      },
      {
        id: 'perils',
        name: 'perils',
        label: 'Perils',
        type: 'select',
        multi: true,
        options: filterOptions['perilOptions'] || [],
      },
      {
        id: 'methodsOfInspection',
        name: 'methodsOfInspection',
        label: 'Method Of Inspection',
        type: 'select',
        multi: true,
        options: filterOptions['methodOfInspectionOptions'] || [],
      },
      {
        id: 'qualifiedResourceIds',
        name: 'qualifiedResourceIds',
        label: 'Qualified resources',
        type: 'async-select',
        multi: true,
        options: [],
        tagLimit: 10,
        isVirtual: true,
      },
      {
        id: 'taskTypes',
        name: 'taskTypes',
        label: 'Task Type',
        type: 'select',
        multi: true,
        options: [
          {
            value: 'CLAIM_OWNER',
            title: 'Claim Owner',
          },
          {
            value: 'NON_COMPLEX',
            title: 'Non Complex',
          },
        ],
      },
      {
        id: 'claimAttributes',
        name: 'claimAttributes',
        label: 'Task Attributes',
        type: 'select',
        multi: true,
        options: attributeOptions?.map((attr) => ({
          value: String(attr.value),
          title: attr.title,
        })) as MultiSelectOption[],
      },
    ];

    if (isAdjuster) {
      filters = filters?.filter(
        (filter) =>
          filter.id !== 'assignedResourceIds' &&
          filter.id !== 'qualifiedResourceIds'
      );
    }

    return filters;
  }, [
    filterOptions,
    filteredResources?.status,
    filteredLossCountiesStatus,
    filteredLossCounties,
    mappedFilteredResources,
    setResourceFilterInput,
    setLossCountyFilterInput,
    isAdjuster,
  ]);

  const getQuickFilters = useCallback(() => {
    let quickFilters: { label: string; value: string; badgeCount?: number }[] =
      [
        { label: 'All', value: 'all' },
        { label: 'Assigned', value: 'assigned' },
      ];

    if (!featureFlags.qualifiedResourcesFnol) {
      quickFilters.push({
        label: 'Unassigned',
        value: 'unassigned',
        badgeCount: unassignedCount,
      });
    }

    if (isAdjuster) {
      quickFilters = [{ label: 'All', value: 'all' }];
    }

    return quickFilters;
  }, [isAdjuster, unassignedCount]);

  useEffect(() => {
    const groupTasks: Record<string, Partial<TaskInfo>[]> = (
      data?.data || []
    )?.reduce(groupTaskReducer, {});
    const geoJsonTasks: GeoJSONObject[] =
      Object.entries(groupTasks).flatMap(itemToGeoJSONPoint);

    mapContext.setPopupInfo(null);
    mapContext.setGeoJson(geoJsonTasks);
  }, [data]);

  useEffect(() => {
    const resourceLocations: {
      starting?: ResourceLocation;
      working?: ResourceLocation[];
    } = {};

    if (resource) {
      const geoJsonResource = ResourceLocationToGeoJSONPoint(resource);
      if (geoJsonResource?.activeWorkLocation?.length > 0) {
        resourceLocations.working = geoJsonResource.activeWorkLocation;
      }

      if (
        geoJsonResource?.activeStartingLocation?.properties?.['center'][0] !==
          0 &&
        geoJsonResource?.activeStartingLocation?.properties?.['center'][1] !== 0
      ) {
        resourceLocations.starting = geoJsonResource.activeStartingLocation;
      }
      mapContext.setResourceLocationGeoJson(resourceLocations);
    }
  }, [
    resource,
    resourceStatus,
    mapContext.selectedResourceId,
    viewState.showMap,
    mapContext.showResourceLocationLayer,
  ]);

  React.useEffect(() => {
    if (multiSelected && multiSelected?.length > 0) {
      mapContext.setResourceLocationGeoJson({});
    }
  }, [multiSelected]);

  const dynamicFilters = [
    {
      label: 'Task Filters',
      data: dynamicFilterOptions,
      contextKey: 'taskFilters',
    },
  ];

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportValidationSchema = yup.object().shape({
    fileName: yup.string().required('File name is required'),
  });

  const { notifySuccess, notifyError } = useNotification();

  const [showExportModal, setShowExportModal] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { fileName: '', format: ReportFormat.Excel },
    resolver: yupResolver(exportValidationSchema),
  });
  const { setIsReportPollingEnabled } = useExportNotification();

  const onExportReport = (response: { success: boolean }) => {
    if (response.success) {
      setIsReportPollingEnabled(true);
      notifySuccess('Success', 'Report job started');
    } else {
      notifyError('Error', 'Report not created, please try again');
    }

    setShowExportModal(false);
    reset();
  };

  const { exportReport, status: exportStatus } =
    useExportReport(onExportReport);

  const onClickExport = ({
    fileName,
    format,
  }: {
    fileName: string;
    format: ReportFormat;
  }) => {
    exportReport({
      request: {
        userId,
        reportType: ReportType.Task,
        fileName,
        format,
        filterQuery: finalFilters,
      },
    });
  };

  const FNOLUrl = React.useMemo(() => {
    if (featureFlags.qualifiedResourcesFnol) {
      return '/tasks/add-qualified';
    }
    return '/tasks/add';
  }, [featureFlags.qualifiedResourcesFnol]);

  return (
    <PageLayout>
      <PageHeader>
        <PageHeaderRow underline>
          <PageTitle
            title="Tasks"
            onSearch={(value) => {
              return onSearch(value);
            }}
            placeHolderText="Claim Number"
            searchInitialValue={
              filters?.claimNumberRaw ||
              filters?.claimNumber ||
              filters?.taskFilters?.claimNumber ||
              ''
            }
          />
          <PageActionGroup>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ backgroundColor: 'grey.900' }}
            />
            <FormControlLabel
              control={
                <Switch
                  color="success"
                  name="isMapView"
                  checked={viewState.showMap}
                  onChange={(e) => {
                    setViewState({ ...viewState, showMap: e.target.checked });
                  }}
                />
              }
              label="Map"
            />
          </PageActionGroup>
        </PageHeaderRow>
        <PageHeaderRow>
          <QuickFilters
            defaultValue="all"
            filterKey="assignedStatus"
            quickFilters={getQuickFilters()}
          />
          <PageActionGroup>
            <DynamicFilterModal<TaskListFilterGroup>
              filters={dynamicFilters}
              showFilterState={showFilterState}
              storedFilters={storedFilters}
              handleSaveFilters={handleSaveFilters}
              handleOrderIndexBulkUpdate={handleOrderIndexBulkUpdate}
              handleGroupsOrderIndexBulkUpdate={
                handleGroupsOrderIndexBulkUpdate
              }
              handleSaveFilterGroup={handleSaveFilterGroup}
              handleDeleteFilter={handleDeleteFilter}
              handleDeleteFilterGroup={handleDeleteFilterGroup}
              setLossStateFilterInput={setLossStateFilterInput}
              setRsrcStateFilterInput={setRsrcStateFilterInput}
              allowSaveFilters={true}
              syncRoutes={['tasks', 'scheduler']}
            />

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              {(isAdjuster || permissions?.can('update', 'task')) && (
                <MenuItem onClick={handleClose}>
                  <Button
                    style={{ width: '100%', display: 'flex' }}
                    onClick={() =>
                      navigate('/tasks/bulk-cancel', {
                        state: {
                          height: '90%',
                          width: '100%',
                          pagination: getPaginationConfig(
                            listFilters.pageSize || 25
                          ),
                          rowHeight: 32,
                          rows: data?.data || [],
                          loading: selectedResourcesLoading,
                        },
                      })
                    }
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    disabled={
                      data?.status === 'loading' || selectedResourcesLoading
                    }
                  >
                    Bulk Cancel
                  </Button>
                </MenuItem>
              )}
              {permissions?.can('update', 'task') && (
                <MenuItem onClick={handleClose}>
                  <Button
                    style={{ width: '100%', display: 'flex' }}
                    onClick={() =>
                      navigate('/tasks/bulk-lock', {
                        state: {
                          height: '90%',
                          width: '100%',
                          pagination: getPaginationConfig(
                            listFilters.pageSize || 25
                          ),
                          rowHeight: 32,
                          rows: data?.data || [],
                          loading: selectedResourcesLoading,
                        },
                      })
                    }
                    variant="outlined"
                    startIcon={<LockIcon />}
                    disabled={
                      data?.status === 'loading' || selectedResourcesLoading
                    }
                  >
                    Bulk Lock
                  </Button>
                </MenuItem>
              )}
              {permissions?.can('create', 'task') && (
                <MenuItem onClick={handleClose}>
                  <Button
                    style={{ width: '100%', display: 'flex' }}
                    onClick={() => navigate(FNOLUrl)}
                    variant="outlined"
                    startIcon={<AddIcon />}
                  >
                    Create Task
                  </Button>
                </MenuItem>
              )}
              {permissions?.can('run', 'optimizer') && (
                <MenuItem onClick={handleClose}>
                  <Button
                    style={{ width: '100%', display: 'flex' }}
                    variant="outlined"
                    onClick={() => {
                      optimize({ taskTypes: ['CLAIM_OWNER'] });
                    }}
                    disabled={optimizeStatus === 'loading'}
                  >
                    <i
                      className="fa-solid fa-people-arrows"
                      style={{ marginRight: '5%' }}
                    />
                    Optimize
                  </Button>
                </MenuItem>
              )}
              {permissions?.can('create', 'reports') && (
                <MenuItem onClick={handleClose}>
                  <Button
                    style={{ width: '100%', display: 'flex' }}
                    variant="outlined"
                    onClick={() => setShowExportModal(true)}
                    disabled={exportStatus === 'loading'}
                    startIcon={<FileDownload />}
                  >
                    Export Tasks
                  </Button>
                </MenuItem>
              )}
            </Menu>
            {showExportModal && (
              <ConfirmModal
                modalType={'export'}
                confirmTitle="Export Tasks"
                confirmContent={
                  <Form<{ fileName: string; format: ReportFormat }>
                    control={control}
                    errors={errors}
                    handleSubmit={handleSubmit}
                    render={({ control, errors }: RenderFuncProps) => (
                      <Stack>
                        <div>
                          <TextField
                            id="fileName"
                            name="fileName"
                            label="File name:"
                            control={control}
                            errors={errors}
                            required
                          />
                          <SelectField
                            id="format"
                            name="format"
                            label="File Type"
                            control={control}
                            errors={errors}
                            defaultValue={ReportFormat.Excel}
                            required
                            options={[
                              {
                                title: ReportFormat.CSV,
                                value: ReportFormat.CSV,
                              },
                              {
                                title: ReportFormat.Excel,
                                value: ReportFormat.Excel,
                              },
                            ]}
                          />
                        </div>
                      </Stack>
                    )}
                  />
                }
                onConfirm={handleSubmit(onClickExport)}
                onDeny={() => {
                  setShowExportModal(false);
                  reset();
                }}
                confirmColor="primary"
              />
            )}
            <Button
              data-testid="bulk-action-menu-button"
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleClick}
            >
              <MoreHorizIcon />
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                // prevent refreshing while data is already being loaded.
                if (fetching <= 0) {
                  onRefresh();
                }
              }}
            >
              <RefreshIcon />
            </Button>
          </PageActionGroup>
        </PageHeaderRow>
      </PageHeader>
      <PageBody>
        <Grid container spacing={2}>
          <Grid
            item
            xs={viewState.showMap ? 9 : true}
            data-testid="tasks-table"
            className="task-content"
          >
            <TableComponent {...tableArgs} />
          </Grid>

          <Slide
            direction="left"
            in={viewState.showMap}
            mountOnEnter
            unmountOnExit
          >
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Stack
                width="100%"
                alignItems="center"
                justifyContent="center"
                sx={{
                  border: 1,
                  borderRadius: 1,
                  borderColor: 'white',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#FFFFFF',
                  position: 'relative',
                }}
                data-testid="tasks-map"
              >
                {selectedResourcesLoading && (
                  <div
                    style={{
                      zIndex: 1000,
                      position: 'absolute',
                    }}
                  >
                    <Loading size="5vh" />
                  </div>
                )}
                <DispatchMap
                  multiSelected={multiSelected}
                  view={viewState}
                  loading={selectedResourcesLoading}
                />
              </Stack>
            </Grid>
          </Slide>
        </Grid>
      </PageBody>
    </PageLayout>
  );
}

export default TasksList;
